/**
 * sl-k94y.5.1: `useLocalePicker` — bound `pickTitle` / `pickDescription`
 * helpers that close over `useLocaleSettings` + `useI18n().language`.
 *
 * Why: `pickTitle(map, language, settings)` is the right pure-function
 * signature for non-React callers (seeds, server-rendered prompts, etc.),
 * but inside React components every single call site would otherwise have
 * to wire up `useI18n()` AND `useLocaleSettings()` AND a loading guard.
 * The hook collapses that into a single subscription and returns `null`
 * until both are ready — which lets render code stay terse:
 *
 *   const picker = useLocalePicker();
 *   if (!picker) return <Skeleton />;
 *   return <h1>{picker.pickTitle(template.titlesI18n)}</h1>;
 *
 * Loading-state contract: the hook returns `null` ONLY while the locale
 * settings query is still loading. If the backend/network is unavailable,
 * local-first surfaces such as Marketplace and Workflows must still render
 * cached/global content. In that case the hook binds a local baseline locale
 * policy instead of throwing into the app error boundary. Strict template
 * corruption still fails loud via `pickTitleStrict`.
 */
import { useI18n } from '@affine/i18n';
import { useMemo } from 'react';

import {
  type LocaleSettings,
  MissingTitleDefaultLocale,
  pickDescription,
  pickTitle,
  pickTitleStrict,
} from '../desktop/dialogs/marketplace/locale-utils';
import { useLocaleSettings } from './use-locale-settings';

export interface BoundLocalePicker {
  readonly settings: LocaleSettings;
  readonly language: string;
  pickTitle(map: Record<string, string> | null | undefined): string;
  pickDescription(map: Record<string, string> | null | undefined): string;
  pickTitleStrict(map: Record<string, string> | null | undefined): string;
}

export class LocaleSettingsUnavailableError extends Error {
  constructor(cause: Error) {
    super(`LocaleSettingsUnavailableError: ${cause.message}`);
    this.name = 'LocaleSettingsUnavailableError';
    this.cause = cause;
  }
}

const LocalFirstLocaleSettings: LocaleSettings = {
  availableLocales: ['en'],
  defaultLocale: 'en',
};

export function useLocalePicker(): BoundLocalePicker | null {
  const i18n = useI18n();
  const { settings, isLoading, error } = useLocaleSettings();

  return useMemo<BoundLocalePicker | null>(() => {
    if (isLoading && !error && !settings) return null;
    const effectiveSettings = settings ?? LocalFirstLocaleSettings;
    const language = i18n.language;
    return {
      settings: effectiveSettings,
      language,
      pickTitle: map => pickTitle(map, language, effectiveSettings),
      pickDescription: map => pickDescription(map, language, effectiveSettings),
      pickTitleStrict: map => pickTitleStrict(map, language, effectiveSettings),
    };
  }, [settings, isLoading, error, i18n.language]);
}

export { MissingTitleDefaultLocale };
